import { FC, useEffect, useMemo, useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { SmallLineChart } from "components/Charts/SmallLineChart";
import { FeedbackType } from "types/feedback";
import { Requirement } from "types/requirement";
import { User } from "types/user";
import { DomainColorSquares } from "components/Requirements";
import {
  buildLearnerPerformanceSeries,
  isTrackedRequirementType,
} from "../learnersAnalytics";

type Props = {
  learners: User[];
  requirements: Requirement[];
  feedbacks: FeedbackType[];
};

export const LearnerPerformanceChart: FC<Props> = ({
  learners,
  requirements,
  feedbacks,
}) => {
  const availableRequirements = useMemo(
    () =>
      requirements
        .filter(
          (requirement) =>
            Boolean(requirement.requirement_id) &&
            isTrackedRequirementType(requirement.type),
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [requirements],
  );
  const [requirementId, setRequirementId] = useState("");

  useEffect(() => {
    if (
      !availableRequirements.some(
        (requirement) => requirement.requirement_id === requirementId,
      )
    ) {
      setRequirementId(availableRequirements[0]?.requirement_id || "");
    }
  }, [availableRequirements, requirementId]);

  const selectedRequirement = availableRequirements.find(
    (requirement) => requirement.requirement_id === requirementId,
  );
  const series = useMemo(
    () =>
      requirementId
        ? buildLearnerPerformanceSeries(requirementId, learners, feedbacks)
        : [],
    [feedbacks, learners, requirementId],
  );

  if (availableRequirements.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        This organization has no score-based requirements.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "flex-start" }}
        flexDirection={{ xs: "column", md: "row" }}
        gap={2}
      >
        <Box>
          <Typography variant="h5">Learner performance</Typography>
          <Typography variant="body2" color="text.secondary">
            Coach rubric scores from completed feedback requests over time.
            Each line represents one learner.
          </Typography>
          {typeof selectedRequirement?.min_score === "number" && (
            <Typography variant="caption" color="text.secondary">
              Target Score On Rubric: {selectedRequirement.min_score}
            </Typography>
          )}
        </Box>
        <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 320 } }}>
          <InputLabel id="learner-performance-requirement-label">
            Requirement
          </InputLabel>
          <Select
            labelId="learner-performance-requirement-label"
            value={requirementId}
            label="Requirement"
            onChange={(event) => setRequirementId(event.target.value)}
          >
            {availableRequirements.map((requirement) => (
              <MenuItem
                key={requirement.requirement_id}
                value={requirement.requirement_id}
              >
                <Stack spacing={0.5} alignItems="flex-start">
                  <span>{requirement.name}</span>
                  <DomainColorSquares requirement={requirement} />
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <SmallLineChart series={series} height={440} />
    </Stack>
  );
};
